/**
 * Display helpers for the Dívidas cluster — liberation framing, effort bands.
 * Sanitise before rendering; never show absurd percentages.
 */

const MONTHS_PT = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/** % of original debt already paid (liberation progress). */
export function liberationProgress({ originalTotal, remainingTotal }) {
  const original = Math.max(0, Number(originalTotal) || 0);
  const remaining = Math.max(0, Number(remainingTotal) || 0);
  if (original <= 0) {
    return { pctPaid: 0, paidAmount: 0, remainingAmount: remaining, hasProgress: false };
  }
  const paid = Math.max(0, original - remaining);
  const raw = (paid / original) * 100;
  const pctPaid = Math.min(100, Math.max(0, Math.round(raw)));
  return {
    pctPaid,
    paidAmount: Math.round(paid * 100) / 100,
    remainingAmount: Math.round(remaining * 100) / 100,
    hasProgress: pctPaid > 0,
  };
}

/** Short month + year, e.g. "setembro 2027". */
export function formatFreeEta(isoDateOrMonths) {
  if (isoDateOrMonths == null || isoDateOrMonths === '') return '';
  if (typeof isoDateOrMonths === 'number' || /^\d+$/.test(String(isoDateOrMonths))) {
    const months = Math.max(0, Number(isoDateOrMonths));
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return `${MONTHS_PT[d.getMonth()]} ${d.getFullYear()}`;
  }
  const parsed = new Date(isoDateOrMonths);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${MONTHS_PT[parsed.getMonth()]} ${parsed.getFullYear()}`;
}

/**
 * Effort-rate bands (knowledge/14-dividas-gestao.md):
 * <30 comfortable · 30–40 tight · >40 risky
 */
export function effortVerdictBand(rate) {
  const n = Number(rate);
  if (Number.isNaN(n) || n < 0) return 'comfortable';
  if (n < 30) return 'comfortable';
  if (n <= 40) return 'tight';
  return 'risky';
}

/**
 * Suggest a monthly payment that keeps after-rate at targetPct (default 37).
 * Returns null if income missing or already under target without change.
 */
export function suggestPaymentForBand({
  existingPayments,
  income,
  proposedPayment,
  targetPct = 37,
}) {
  const incomeN = Number(income);
  const existing = Math.max(0, Number(existingPayments) || 0);
  const proposed = Math.max(0, Number(proposedPayment) || 0);
  if (!(incomeN > 0) || proposed <= 0) return null;

  const after = ((existing + proposed) / incomeN) * 100;
  if (after <= targetPct) return null;

  const maxTotal = (incomeN * targetPct) / 100;
  const suggested = Math.max(0, Math.round((maxTotal - existing) * 100) / 100);
  if (suggested <= 0 || suggested >= proposed) return null;
  return suggested;
}

/** Clamp progress_pct from API for display. */
export function sanitizeProgressPct(value) {
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.min(100, Math.round(n));
}

/** Aggregate liberation from a list of active liabilities. */
export function aggregateLiberation(liabilities = []) {
  let originalTotal = 0;
  let remainingTotal = 0;
  for (const l of liabilities) {
    const remaining = Number(l.current_balance || 0);
    const original = Number(l.original_amount || l.debt_amount || remaining);
    originalTotal += Math.max(original, remaining);
    remainingTotal += remaining;
  }
  return liberationProgress({ originalTotal, remainingTotal });
}

/** Max months across plan items (proxy for "free in"). */
export function maxPayoffMonthsFromPlan(plan) {
  const items = plan?.items || [];
  let max = 0;
  for (const item of items) {
    const m = Number(item.payoff_months);
    if (!Number.isNaN(m) && m > max) max = m;
  }
  return max > 0 ? max : null;
}

/** Months until first debt is paid off (priority #1 in ordered plan). */
export function firstVictoryMonthsFromPlan(plan) {
  const first = plan?.items?.[0];
  if (!first) return null;
  const m = Number(first.payoff_months);
  if (Number.isNaN(m) || m <= 0) return null;
  return m;
}

export function methodBenefitKey(method) {
  return method === 'snowball' ? 'snowball' : 'avalanche';
}
